import { useState } from 'react'
import { PlayCircle, GitCommitHorizontal, CircleDot, Link2, Boxes, Bot, ChevronDown, ChevronRight } from 'lucide-react'
import {
  SearchIcon,
  CodeBlocksIcon,
  GitCloneIcon,
  TextSnippetIcon,
  EditPenIcon,
  ValidateConnectorIcon,
  NetworkNodeIcon,
  PromptsIcon,
} from '@/components/icons'
import { Input } from '@/components/ui/input'
import { useAgentVersions } from '@/hooks'
import type { Agent, NodeTypeCategory, NodeTypeDescriptor, Prompt, Connector } from '@/types'
import type { SandboxImage } from '@/types/sandboxImage'

interface BuildingBlocksPaletteProps {
  nodeTypes?: NodeTypeDescriptor[]
  prompts?: Prompt[]
  connectors?: Connector[]
  sandboxImages?: SandboxImage[]
  projectId?: string
  agents?: Agent[]
  onInsertSnippet: (snippet: string) => void
}

const CATEGORY_CONFIG: Record<NodeTypeCategory, { label: string }> = {
  AI: { label: 'AI' },
  Development: { label: 'DEVELOPMENT' },
  Integration: { label: 'INTEGRATION' },
  Utilities: { label: 'UTILITIES' },
}

const BLOCK_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'claude-code': CodeBlocksIcon,
  'run-command': PlayCircle,
  'git-clone': GitCloneIcon,
  'git-commit-push': GitCommitHorizontal,
  'fetch-work-item': TextSnippetIcon,
  'create-change-set': EditPenIcon,
  'apply-change-set': GitCommitHorizontal,
  'validate-connector': ValidateConnectorIcon,
  'evaluate-condition': NetworkNodeIcon,
}

const CATEGORIES: NodeTypeCategory[] = ['AI', 'Development', 'Integration', 'Utilities']

export function BuildingBlocksPalette({ nodeTypes = [], prompts = [], connectors = [], sandboxImages = [], projectId, agents = [], onInsertSnippet }: BuildingBlocksPaletteProps) {
  const [search, setSearch] = useState('')
  const q = search.toLowerCase()

  const filteredByCategory = CATEGORIES.map((category) => {
    const blocks = nodeTypes.filter((b) => {
      if (b.category !== category) return false
      if (q) {
        return b.displayName.toLowerCase().includes(q) || b.description.toLowerCase().includes(q)
      }
      return true
    })
    return { category, blocks }
  }).filter((g) => g.blocks.length > 0)

  const filteredSandboxImages = sandboxImages
    .filter((img) => img.kind === 'Workflow' && !img.isDeprecated)
    .filter((img) =>
      !q
      || img.name.toLowerCase().includes(q)
      || img.imageUri.toLowerCase().includes(q)
      || (img.runtimes?.toLowerCase().includes(q) ?? false)
    )

  const filteredPrompts = prompts.filter((p) =>
    !q || p.name.toLowerCase().includes(q) || (p.description?.toLowerCase().includes(q) ?? false) || p.internalName.toLowerCase().includes(q)
  )

  const filteredConnectors = connectors.filter((c) =>
    c.category !== 'Database' && c.category !== 'CodeQuality' && c.category !== 'AiProvider' && (!q || c.type.toLowerCase().includes(q))
  )

  const filteredAgents = agents.filter((a) =>
    !q || a.displayName.toLowerCase().includes(q) || a.name.toLowerCase().includes(q) || (a.description?.toLowerCase().includes(q) ?? false)
  )

  const hasAnyData = nodeTypes.length > 0 || sandboxImages.length > 0 || prompts.length > 0 || connectors.length > 0 || agents.length > 0

  return (
    <div className="flex h-full flex-col bg-card rounded-2xl border border-border">
      <div className="p-4 border-b border-border space-y-4">
        <h3 className="text-lg text-foreground font-bold leading-[1.4] tracking-[0.015em]">Building Blocks</h3>
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-foreground" />
          <Input
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-10 rounded-lg border-border pl-10"
            aria-label="Search building blocks"
            data-testid="building-blocks-search-input"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {filteredByCategory.map(({ category, blocks }) => {
          const config = CATEGORY_CONFIG[category]
          return (
            <div key={category} className="mt-4 first:mt-0">
              <h4 className="text-xs text-foreground font-bold uppercase leading-[1.6] tracking-[0.015em] mb-1">
                {config.label}
              </h4>
              <div>
                {blocks.map((block) => {
                  const BlockIcon = BLOCK_ICONS[block.typeName] ?? CircleDot
                  return (
                    <button
                      key={block.typeName}
                      className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-muted hover:text-muted-foreground cursor-pointer"
                      onClick={() => onInsertSnippet(block.yamlSnippet)}
                      title={block.description}
                      data-testid={`building-block-${block.typeName}`}
                    >
                      <BlockIcon className="h-4 w-4 shrink-0 text-foreground" />
                      <span className="font-normal text-sm leading-[1.6] tracking-[0.015em] text-foreground">{block.displayName}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
        {filteredSandboxImages.length > 0 && (
          <div className="mt-4">
            <h4 className="text-xs font-bold uppercase leading-[1.6] tracking-[0.015em] text-foreground">
              SANDBOX IMAGES
            </h4>
            <div>
              {filteredSandboxImages.map((image) => (
                <button
                  key={image.id}
                  className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-muted cursor-pointer"
                  onClick={() => onInsertSnippet(`sandboxImage: ${image.imageUri}\n`)}
                  title={image.description}
                  data-testid={`building-block-sandbox-image-${image.id}`}
                >
                  <Boxes className="h-4 w-4 shrink-0 text-foreground" />
                  <span className="font-normal text-sm leading-[1.6] tracking-[0.015em] text-foreground">{image.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {filteredPrompts.length > 0 && (
          <div className="mt-4">
            <h4 className="text-xs font-bold uppercase leading-[1.6] tracking-[0.015em] text-foreground mb-1">
              PROMPTS
            </h4>
            <div>
              {filteredPrompts.map((prompt) => (
                <button
                  key={prompt.id}
                  className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-muted cursor-pointer"
                  onClick={() => onInsertSnippet(`refs/prompts/${prompt.internalName}`)}
                  title={prompt.description ?? prompt.name}
                  data-testid={`building-block-prompt-${prompt.internalName}`}
                >
                  <PromptsIcon className="h-4 w-4 shrink-0 text-foreground" />
                  <span className="font-normal text-sm leading-[1.6] tracking-[0.015em] text-foreground">{prompt.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {projectId && filteredAgents.length > 0 && (
          <div className="mt-4">
            <h4 className="text-xs font-bold uppercase leading-[1.6] tracking-[0.015em] text-foreground mb-1">
              AGENTS
            </h4>
            <div>
              {filteredAgents.map((agent) => (
                <PaletteAgentRow key={agent.id} projectId={projectId} agent={agent} onInsertSnippet={onInsertSnippet} />
              ))}
            </div>
          </div>
        )}
        {filteredConnectors.length > 0 && (
          <div className="mt-4">
            <h4 className="text-xs font-bold uppercase leading-[1.6] tracking-[0.015em] text-foreground mb-1">
              CONNECTORS
            </h4>
            <div>
              {filteredConnectors.map((connector) => (
                <button
                  key={connector.id}
                  className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-muted cursor-pointer"
                  onClick={() => onInsertSnippet(`refs/connectors/${connector.type}`)}
                  title={`Connector: ${connector.type}`}
                  data-testid={`building-block-connector-${connector.type}`}
                >
                  <Link2 className="h-4 w-4 shrink-0 text-foreground" />
                  <span className="font-normal text-sm leading-[1.6] tracking-[0.015em] text-foreground">{connector.type}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {filteredByCategory.length === 0 && filteredSandboxImages.length === 0 && filteredPrompts.length === 0 && filteredConnectors.length === 0 && filteredAgents.length === 0 && hasAnyData && (
          <p className="text-center text-sm text-muted-foreground py-4">No blocks found</p>
        )}
        {!hasAnyData && (
          <p className="text-center text-sm text-muted-foreground py-4" role="status" aria-live="polite">Loading building blocks...</p>
        )}
      </div>
    </div>
  )
}

interface PaletteAgentRowProps {
  projectId: string
  agent: Agent
  onInsertSnippet: (snippet: string) => void
}

function PaletteAgentRow({ projectId, agent, onInsertSnippet }: PaletteAgentRowProps) {
  const [expanded, setExpanded] = useState(false)
  const { data: versions = [], isLoading, isError } = useAgentVersions(projectId, expanded ? agent.id : undefined)
  const ChevronIcon = expanded ? ChevronDown : ChevronRight

  return (
    <div>
      <div className="flex w-full items-center gap-1 rounded-md transition-colors hover:bg-muted">
        <button
          className="flex min-w-0 flex-1 items-center gap-2.5 px-2 py-2 text-left text-sm cursor-pointer"
          onClick={() => onInsertSnippet(`agent: ${agent.name}@latest`)}
          title={agent.description ?? agent.displayName}
          data-testid={`building-block-agent-${agent.name}`}
        >
          <Bot className="h-4 w-4 shrink-0 text-foreground" />
          <span className="truncate font-normal text-sm leading-[1.6] tracking-[0.015em] text-foreground">{agent.displayName}</span>
        </button>
        <button
          className="shrink-0 rounded-md p-2 text-muted-foreground hover:text-foreground cursor-pointer"
          onClick={() => setExpanded(open => !open)}
          aria-label={`${expanded ? 'Hide' : 'Show'} versions of ${agent.displayName}`}
          aria-expanded={expanded}
          data-testid={`building-block-agent-${agent.name}-versions-toggle`}
        >
          <ChevronIcon className="h-4 w-4" />
        </button>
      </div>
      {expanded && (
        <div className="ml-6 border-l border-border pl-2">
          {isLoading ? (
            <p className="px-2 py-1 text-xs text-muted-foreground">Loading versions…</p>
          ) : isError ? (
            <p className="px-2 py-1 text-xs text-destructive">Couldn't load versions.</p>
          ) : versions.length === 0 ? (
            <p className="px-2 py-1 text-xs text-muted-foreground">No versions yet.</p>
          ) : (
            versions.map((version) => (
              <button
                key={version.id}
                className="flex w-full items-center rounded-md px-2 py-1 text-left font-mono text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground cursor-pointer"
                onClick={() => onInsertSnippet(`agent: ${agent.name}@${version.version}`)}
                data-testid={`building-block-agent-${agent.name}-version-${version.version}`}
              >
                @{version.version}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
